import React, { useState } from 'react';
import { View, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Text from '../common/Text';
import { useTheme } from '../../hooks/useTheme';

export interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  label?: string;
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select an option',
}) => {
  const { colors } = useTheme();
  const [showDropdown, setShowDropdown] = useState(false);
  const [hoveredValue, setHoveredValue] = useState<string | null>(null);

  const selectedOption = options.find((opt) => opt.value === value) || null;

  return (
    <View style={styles.dropdownContainer}>
      {label && (
        <Text style={[styles.label, { color: colors.text }]}>{label}</Text>
      )}
      <TouchableOpacity
        style={[
          styles.dropdown,
          { backgroundColor: colors.background, borderColor: colors.border },
        ]}
        onPress={() => setShowDropdown(!showDropdown)}
      >
        <Text style={[styles.dropdownText, { color: colors.text }]}>
          {selectedOption ? selectedOption.label : placeholder}
        </Text>
        <Ionicons
          name={showDropdown ? 'chevron-up' : 'chevron-down'}
          size={16}
          color={colors.secondary}
        />
      </TouchableOpacity>

      {showDropdown && (
        <View
          style={[
            styles.dropdownMenu,
            { backgroundColor: colors.card, borderColor: colors.border },
          ]}
        >
          {options.map((option) => {
            const isSelected = value === option.value;
            const isHovered = hoveredValue === option.value;

            let backgroundColor = 'transparent';
            if (isSelected) {
              backgroundColor = colors.primary + '20';
            } else if (isHovered) {
              backgroundColor = colors.primary + '10';
            }

            return (
              <TouchableOpacity
                key={option.value}
                style={[styles.dropdownItem, { backgroundColor }]}
                onPress={() => {
                  onChange(option.value);
                  setShowDropdown(false);
                }}
                activeOpacity={0.7}
                // @ts-ignore - onMouseEnter/onMouseLeave are available on web
                onMouseEnter={() => setHoveredValue(option.value)}
                onMouseLeave={() => setHoveredValue(null)}
              >
                <Text style={[styles.dropdownItemText, { color: colors.text }]}>
                  {option.label}
                </Text>
                {isSelected && (
                  <Ionicons name="checkmark" size={16} color={colors.primary} />
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  dropdownContainer: {
    position: 'relative',
    zIndex: 10,
  },
  label: {
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 4,
  },
  dropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    minHeight: 32,
  },
  dropdownText: {
    fontSize: 14,
    flex: 1,
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    borderRadius: 6,
    borderWidth: 1,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 5,
      },
    }),
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  dropdownItemText: {
    fontSize: 14,
    flex: 1,
  },
});

export default Dropdown;
